import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface Supervisor {
  Id: number;
  FirstName: string;
  LastName: string;
  Name?: string; // Computed field: FirstName + ' ' + LastName
  IsSupervisor: boolean;
}

export interface Operator {
  Id: number;
  Name: string;
  EmployeeNumber: string;
  SupervisorId: number | null;
  Supervisor?: Supervisor;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface CreateOperatorRequest {
  Name: string;
  EmployeeNumber: string;
  SupervisorId: number | null;
  Active: boolean;
}

export interface UpdateOperatorRequest {
  Id: number;
  Name: string;
  EmployeeNumber: string;
  SupervisorId: number | null;
  Active: boolean;
}

export interface OperatorListResponse {
  data: Operator[];
  total: number;
}

export interface OperatorResponse {
  data: Operator;
}

function ensureArray<T>(value: any): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value == null) return [] as T[];
  return [value as T];
}

// API Service
export const operatorsApi = {
  // CRUD Operations
  getAll: async (): Promise<Operator[]> => {
    const result = await api.get<any>('/operators');
    return ensureArray<Operator>(result);
  },

  getById: (id: number): Promise<Operator> =>
    api.get(`/operators/${id}`),

  create: (operator: CreateOperatorRequest): Promise<Operator> =>
    api.post('/operators', operator),

  update: (operator: UpdateOperatorRequest): Promise<Operator> =>
    api.put(`/operators/${operator.Id}`, operator),

  delete: (id: number): Promise<void> =>
    api.delete(`/operators/${id}`),

  // Specific Operations
  getSupervisors: async (): Promise<Supervisor[]> => {
    const res = await api.get<any>('/user', { IsSupervisor: true });
    const supervisors = ensureArray<Supervisor>(res);
    return supervisors.map((supervisor: Supervisor) => ({
      ...supervisor,
      Name: `${supervisor.FirstName} ${supervisor.LastName}`
    }));
  },

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<void> =>
    api.post('/operators/bulk/activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<void> =>
    api.post('/operators/bulk/deactivate', { ids }),

  // Statistics
  getStatistics: (): Promise<{ total: number; active: number; inactive: number }> =>
    api.get('/operators/statistics'),

  // Validation helpers
  validation: {
    validateOperator: (operator: Partial<CreateOperatorRequest> | Partial<UpdateOperatorRequest> | Partial<Operator>): string[] => {
      const errors: string[] = [];

      if (!operator.Name || operator.Name.trim().length === 0) {
        errors.push('Name is required');
      }

      if (!operator.EmployeeNumber || operator.EmployeeNumber.trim().length === 0) {
        errors.push('Employee Number is required');
      }

      // Check for duplicate employee numbers (would need to be implemented on backend)
      if (operator.EmployeeNumber && operator.EmployeeNumber.trim().length > 0) {
        if (!/^[A-Za-z0-9\-_]+$/.test(operator.EmployeeNumber)) {
          errors.push('Employee Number must contain only alphanumeric characters, hyphens, and underscores');
        }
      }

      return errors;
    },

    validateBeforeSave: (operator: CreateOperatorRequest | UpdateOperatorRequest): string[] => {
      const errors = operatorsApi.validation.validateOperator(operator);

      // Additional validation for save operations
      if (operator.SupervisorId && typeof operator.SupervisorId !== 'number') {
        errors.push('Please select a valid supervisor');
      }

      return errors;
    }
  }
};

export default operatorsApi; 